import HPMigrationUtility from './HPMigrationUtility';
import { hydrateCharacterCombatState } from './CharacterCombatReadiness';
import {
  buildShortAbilityBlock,
  firstPresentValue,
  resolveCombatantProficiencyBonus,
  resolveCombatantSpeedFeet,
  resolvePreferredAbilityScores,
  resolvePreferredArmorClass,
  resolvePreferredInitiative,
  toFiniteNumber
} from './combat/CombatantNormalizer';
import {
  resolveCharacterArmorClass,
  resolveCharacterDefensesAndSenses,
  resolveCharacterHitDice
} from './CharacterRulesPresentation';

const DEFAULT_CHARACTER_SHEET_DATA = Object.freeze({
  id: 'unknown',
  name: 'Unknown Character',
  type: 'character',
  class: 'Fighter',
  level: 1,
  race: 'Human',
  hp: 10,
  maxHp: 10,
  ac: 10,
  initiative: 0,
  proficiencyBonus: 2,
  speed: 30,
  abilities: {
    strength: 10,
    dexterity: 10,
    constitution: 10,
    intelligence: 10,
    wisdom: 10,
    charisma: 10
  }
});

const asRecord = (value) => (
  value && typeof value === 'object' ? value : null
);

const cloneDefaultCharacterSheetData = () => ({
  ...DEFAULT_CHARACTER_SHEET_DATA,
  abilities: { ...DEFAULT_CHARACTER_SHEET_DATA.abilities },
  stats: buildShortAbilityBlock(DEFAULT_CHARACTER_SHEET_DATA.abilities),
  abilitiesShort: buildShortAbilityBlock(DEFAULT_CHARACTER_SHEET_DATA.abilities)
});

const looksLikeFlatCharacterSheetNode = (source) => Boolean(
  source
  && typeof source === 'object'
  && (
    source.abilities
    || source.equipment
    || source.spells
    || source.spellcasting
    || source.features
    || source.classFeatures
    || source.feats
    || source.speciesTraits
    || source.proficiencies
    || source.savingThrows
    || source.skills
  )
);

const getPrimaryDataNode = (source) => (
  firstPresentValue(
    source?.data,
    source?.schemaData,
    source?.characterData?.data,
    source?.characterData?.schemaData,
    source?.canonicalData,
    source?.originalUnifiedEntity?.data,
    source?.compat?.originalData,
    looksLikeFlatCharacterSheetNode(source?.characterData) ? source.characterData : null,
    looksLikeFlatCharacterSheetNode(source) ? source : null
  ) || null
);

const resolveDisplayLevel = (source) => {
  const directLevel = toFiniteNumber(
    firstPresentValue(
      source?.level,
      source?.data?.level,
      source?.characterData?.level,
      source?.canonicalData?.level
    )
  );

  if (directLevel != null) {
    return directLevel;
  }

  const classLists = [
    source?.classes,
    source?.data?.classes,
    source?.characterData?.classes,
    source?.canonicalData?.classes,
    source?.compat?.originalData?.classes
  ];

  for (const classList of classLists) {
    if (!Array.isArray(classList) || classList.length === 0) continue;
    const totalLevels = classList.reduce((sum, cls) => {
      const numeric = toFiniteNumber(cls?.level);
      return numeric != null ? sum + numeric : sum;
    }, 0);
    if (totalLevels > 0) {
      return totalLevels;
    }
  }

  return DEFAULT_CHARACTER_SHEET_DATA.level;
};

const mergeCharacterSources = (sourceData) => {
  if (!sourceData || typeof sourceData !== 'object') {
    return null;
  }

  const embeddedCharacter = asRecord(sourceData.characterData);
  const merged = embeddedCharacter
    ? { ...embeddedCharacter, ...sourceData }
    : { ...sourceData };

  const primaryDataNode = getPrimaryDataNode(sourceData) || getPrimaryDataNode(embeddedCharacter);
  if (primaryDataNode) {
    merged.data = primaryDataNode;
  }

  merged.schemaData = firstPresentValue(
    sourceData.schemaData,
    embeddedCharacter?.schemaData,
    merged.data,
    sourceData.canonicalData,
    embeddedCharacter?.canonicalData
  ) || undefined;

  merged.canonicalData = firstPresentValue(
    sourceData.canonicalData,
    embeddedCharacter?.canonicalData,
    merged.data,
    sourceData.originalUnifiedEntity?.data,
    sourceData.compat?.originalData
  ) || undefined;

  merged.originalUnifiedEntity = sourceData.originalUnifiedEntity || embeddedCharacter?.originalUnifiedEntity || null;
  if (embeddedCharacter) {
    merged.characterData = embeddedCharacter;
  }

  return merged;
};

export const resolveCharacterSheetData = (sourceData) => {
  const mergedSource = mergeCharacterSources(sourceData);
  if (!mergedSource) {
    return cloneDefaultCharacterSheetData();
  }

  const preparedData = HPMigrationUtility.migrateEntity({ ...mergedSource }) || mergedSource;
  const primaryDataNode = getPrimaryDataNode(preparedData) || preparedData.data || null;
  const canonicalSources = [
    primaryDataNode,
    preparedData.schemaData,
    preparedData.canonicalData,
    preparedData.originalUnifiedEntity?.data,
    preparedData.characterData,
    preparedData.characterData?.data,
    preparedData.characterData?.schemaData,
    preparedData.characterData?.canonicalData,
    preparedData.characterData?.originalUnifiedEntity?.data,
    preparedData.compat?.originalData
  ].filter(Boolean);

  const preferredAbilities = resolvePreferredAbilityScores({
    runtimeSources: [
      preparedData.abilities,
      preparedData.stats,
      preparedData
    ],
    canonicalSources,
    runtimeOverrideSource: preparedData,
    fallbackValue: null
  });

  const abilitySeed = Object.keys(preferredAbilities || {}).length > 0
    ? preferredAbilities
    : null;
  const hydratedCharacter = hydrateCharacterCombatState({
    ...preparedData,
    data: primaryDataNode || preparedData.data,
    ...(abilitySeed ? { abilities: abilitySeed } : {})
  });

  const finalAbilities = Object.keys(hydratedCharacter.abilities || {}).length > 0
    ? hydratedCharacter.abilities
    : (abilitySeed || cloneDefaultCharacterSheetData().abilities);
  const shortAbilityBlock = buildShortAbilityBlock(finalAbilities) || buildShortAbilityBlock(DEFAULT_CHARACTER_SHEET_DATA.abilities);
  const resolvedArmorClass = resolvePreferredArmorClass({
    runtimeSources: [preparedData],
    canonicalSources,
    runtimeOverrideSource: preparedData,
    fallbackValue: null
  });
  const resolvedInitiative = resolvePreferredInitiative({
    runtimeSources: [preparedData],
    canonicalSources,
    runtimeOverrideSource: preparedData,
    fallbackValue: null
  });
  const resolvedSpeed = resolveCombatantSpeedFeet(preparedData, canonicalSources);
  const resolvedProficiencyBonus = resolveCombatantProficiencyBonus(preparedData, canonicalSources);
  const presentationSource = {
    ...preparedData,
    ...hydratedCharacter,
    data: primaryDataNode || preparedData.data,
    abilities: finalAbilities,
    proficiencyBonus: resolvedProficiencyBonus ?? hydratedCharacter.proficiencyBonus ?? preparedData.proficiencyBonus
  };
  const armorClassState = resolveCharacterArmorClass(presentationSource);
  const hasDerivedArmorClass = /glamou?red studded leather/i.test(armorClassState.source);
  const hitDiceState = resolveCharacterHitDice(presentationSource);
  const defensesAndSenses = resolveCharacterDefensesAndSenses(presentationSource);

  return {
    ...preparedData,
    ...hydratedCharacter,
    id: preparedData.id || hydratedCharacter.id || DEFAULT_CHARACTER_SHEET_DATA.id,
    name: firstPresentValue(preparedData.name, hydratedCharacter.name, primaryDataNode?.identity?.name, primaryDataNode?.name, DEFAULT_CHARACTER_SHEET_DATA.name),
    type: firstPresentValue(preparedData.type, hydratedCharacter.type, sourceData?.type, DEFAULT_CHARACTER_SHEET_DATA.type),
    class: firstPresentValue(
      hydratedCharacter.class,
      preparedData.class,
      primaryDataNode?.class,
      Array.isArray(primaryDataNode?.classes) ? primaryDataNode.classes[0]?.name : null,
      DEFAULT_CHARACTER_SHEET_DATA.class
    ),
    level: resolveDisplayLevel({
      ...preparedData,
      ...hydratedCharacter,
      data: primaryDataNode || hydratedCharacter.data
    }),
    race: firstPresentValue(
      hydratedCharacter.race,
      preparedData.race,
      primaryDataNode?.identity?.race,
      primaryDataNode?.race,
      DEFAULT_CHARACTER_SHEET_DATA.race
    ),
    background: firstPresentValue(
      hydratedCharacter.background,
      preparedData.background,
      primaryDataNode?.identity?.background,
      primaryDataNode?.background,
      ''
    ),
    alignment: firstPresentValue(
      hydratedCharacter.alignment,
      preparedData.alignment,
      primaryDataNode?.identity?.alignment,
      primaryDataNode?.alignment,
      ''
    ),
    hp: toFiniteNumber(firstPresentValue(
      hydratedCharacter.hp,
      preparedData.hp,
      primaryDataNode?.hitPoints?.current,
      primaryDataNode?.hp?.current,
      primaryDataNode?.hp
    )) ?? DEFAULT_CHARACTER_SHEET_DATA.hp,
    maxHp: toFiniteNumber(firstPresentValue(
      hydratedCharacter.maxHp,
      preparedData.maxHp,
      primaryDataNode?.hitPoints?.maximum,
      primaryDataNode?.hp?.maximum,
      primaryDataNode?.maxHp,
      primaryDataNode?.hp
    )) ?? DEFAULT_CHARACTER_SHEET_DATA.maxHp,
    ac: hasDerivedArmorClass
      ? armorClassState.value
      : (resolvedArmorClass ?? hydratedCharacter.ac ?? preparedData.ac ?? DEFAULT_CHARACTER_SHEET_DATA.ac),
    armorClassSource: hasDerivedArmorClass ? armorClassState.source : 'Recorded Armor Class',
    initiative: resolvedInitiative
      ?? hydratedCharacter.initiative
      ?? preparedData.initiative
      ?? DEFAULT_CHARACTER_SHEET_DATA.initiative,
    speed: resolvedSpeed
      ?? hydratedCharacter.speed
      ?? preparedData.speed
      ?? DEFAULT_CHARACTER_SHEET_DATA.speed,
    proficiencyBonus: resolvedProficiencyBonus
      ?? hydratedCharacter.proficiencyBonus
      ?? preparedData.proficiencyBonus
      ?? DEFAULT_CHARACTER_SHEET_DATA.proficiencyBonus,
    hitDice: hitDiceState.formula,
    hitDiceSpent: hitDiceState.spent,
    hitDiceState,
    derivedDefenses: defensesAndSenses.defenses,
    derivedSenses: defensesAndSenses.senses,
    abilities: finalAbilities,
    stats: shortAbilityBlock,
    abilitiesShort: shortAbilityBlock,
    data: primaryDataNode || hydratedCharacter.data || preparedData.data || {},
    spells: asRecord(hydratedCharacter.spells) || asRecord(preparedData.spells) || asRecord(primaryDataNode?.spells) || {},
    spellcasting: asRecord(hydratedCharacter.spellcasting) || asRecord(preparedData.spellcasting) || asRecord(primaryDataNode?.spellcasting) || {},
    equipment: asRecord(hydratedCharacter.equipment) || asRecord(preparedData.equipment) || asRecord(primaryDataNode?.equipment) || {},
    features: firstPresentValue(hydratedCharacter.features, preparedData.features, primaryDataNode?.features) || [],
    classFeatures: firstPresentValue(hydratedCharacter.classFeatures, preparedData.classFeatures, primaryDataNode?.classFeatures) || [],
    feats: firstPresentValue(hydratedCharacter.feats, preparedData.feats, primaryDataNode?.feats) || [],
    speciesTraits: firstPresentValue(hydratedCharacter.speciesTraits, preparedData.speciesTraits, primaryDataNode?.speciesTraits) || [],
    proficiencies: firstPresentValue(hydratedCharacter.proficiencies, preparedData.proficiencies, primaryDataNode?.proficiencies) || {},
    savingThrows: firstPresentValue(hydratedCharacter.savingThrows, preparedData.savingThrows, primaryDataNode?.savingThrows) || {},
    skills: firstPresentValue(hydratedCharacter.skills, preparedData.skills, primaryDataNode?.skills) || {},
    languages: firstPresentValue(hydratedCharacter.languages, preparedData.languages, primaryDataNode?.languages) || [],
    inventory: firstPresentValue(hydratedCharacter.inventory, preparedData.inventory, primaryDataNode?.inventory) || null,
    items: firstPresentValue(hydratedCharacter.items, preparedData.items, primaryDataNode?.items) || [],
    appearance: firstPresentValue(hydratedCharacter.appearance, preparedData.appearance, primaryDataNode?.appearance, preparedData.biography?.appearance) || null,
    biography: firstPresentValue(hydratedCharacter.biography, preparedData.biography, primaryDataNode?.biography) || null,
    backstory: firstPresentValue(hydratedCharacter.backstory, preparedData.backstory, primaryDataNode?.backstory, preparedData.biography?.backstory) || '',
    relationships: firstPresentValue(hydratedCharacter.relationships, preparedData.relationships, primaryDataNode?.relationships) || [],
    political: firstPresentValue(hydratedCharacter.political, preparedData.political, primaryDataNode?.political) || null,
    storage: firstPresentValue(hydratedCharacter.storage, preparedData.storage, primaryDataNode?.storage) || null,
    notes: firstPresentValue(hydratedCharacter.notes, preparedData.notes, primaryDataNode?.notes) || null
  };
};

export default resolveCharacterSheetData;
